// Wedding-invitation artwork. The photo-matching composition shared by:
//   - Home.jsx (the public landing at `/`)
//   - Confirmacion.jsx (the per-token RSVP page at `/confirmacion/:token`)
//
// Pure presentation: pulls most visible copy from EVENT_DATA (lib/event.js).
// The optional `eventSettings` prop overrides the DB-backed subset
// (event_date / event_time / location.{city,country,maps_url} /
// ceremony.{venue,maps_url} / reception_time / rsvp_deadline — the last
// is admin-editable since v2) when the public fetch resolves; until then
// the artwork renders with the same strings EVENT_DATA encodes (the DB
// seed is byte-identical to those constants) — no flicker, no layout
// shift. Non-editable fields (dressCode, epigraph, couple, etc.) always
// come from EVENT_DATA so a partial prop never wipes read-only copy.
// Ceremony venue and reception time are admin-optional: when the prop is
// missing or empty, the matching row is hidden from the artwork entirely.
//
// Children render below the artwork as an opt-in slot — Confirmacion uses this
// to mount the RSVP form inside the same gold frame so a guest lands on
// "the wedding invitation + a polite form" in one continuous scroll.
//
// Once the RSVP deadline has passed — and only once the settings request has
// resolved — the whole artwork is replaced by a short closed-list notice: the
// guest list is closed, so the celebration itself is no longer advertised.
import { EVENT_DATA } from '../../lib/event.js';
import { isRsvpDeadlinePassed, resolveRsvpDeadlineIso } from '../../lib/rsvpDeadline.js';
import Divider from './Divider.jsx';
import FloralCorner from './FloralCorner.jsx';
import GoldFrame from './GoldFrame.jsx';
import IconLine from './IconLine.jsx';
import QuoteTag from './QuoteTag.jsx';

function formatEventDate(isoDate) {
  // Matches spec FR-5.3: explicit options so the weekday is always
  // included. The es-EC Intl output puts a comma between weekday and
  // day ("sábado, 5 de diciembre de 2026"), but EVENT_DATA.date.humanLong
  // is hand-written without it ("sábado 5 de diciembre de 2026"). We
  // strip the comma so the rendered string is byte-equal to the seed
  // when the date matches — required for the zero-CLS guarantee.
  try {
    const raw = new Intl.DateTimeFormat('es-EC', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: 'America/Guayaquil',
    }).format(new Date(`${isoDate}T00:00:00-05:00`));
    return raw.replace(/,\s*/g, ' ');
  } catch {
    return EVENT_DATA.date.humanShort;
  }
}

function formatEventTime(hhmm) {
  // Spec FR-5.4: "desde las 16h00".
  if (typeof hhmm !== 'string' || !/^\d{2}:\d{2}$/.test(hhmm)) {
    return EVENT_DATA.time.human;
  }
  return `desde las ${hhmm.replace(':', 'h')}`;
}

function formatReceptionTime(hhmm) {
  // Mirrors formatEventTime but with the "A partir de las HHhMM" copy the
  // reception column uses. Empty / invalid input collapses to null so the
  // artwork can hide the row entirely (the reception row is admin-optional).
  if (typeof hhmm !== 'string' || !/^\d{2}:\d{2}$/.test(hhmm)) {
    return null;
  }
  return `A partir de las ${hhmm.replace(':', 'h')}`;
}

function formatRsvpDeadline(isoDate) {
  // The DB stores rsvp_deadline as YYYY-MM-DD ("until end of that day"
  // per the admin contract). Format without weekday — EVENT_DATA's seed
  // humanLong is "10 de octubre de 2026" (no weekday) — so the rendered
  // string stays byte-equal to the seed when the value matches. Same TZ
  // discipline as formatEventDate: parse midnight Ecuador so the calendar
  // day never shifts across server/client timezones.
  if (typeof isoDate !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) {
    return EVENT_DATA.rsvp.deadline.humanLong;
  }
  try {
    return new Intl.DateTimeFormat('es-EC', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: 'America/Guayaquil',
    }).format(new Date(`${isoDate}T00:00:00-05:00`));
  } catch {
    return EVENT_DATA.rsvp.deadline.humanLong;
  }
}

export default function InvitationArtwork({
  children,
  showFlorals = true,
  eventSettings,
  preBlessing,
  confirmedInvitationWindowActive,
  hideRsvpPrompt = false,
}) {
  const { epigraph, couple, bodyParagraph, date, time, location, dressCode, rsvp, closing } =
    EVENT_DATA;

  const dateHuman = eventSettings ? formatEventDate(eventSettings.event_date) : date.humanLong;
  const timeHuman = eventSettings ? formatEventTime(eventSettings.event_time) : time.human;
  const locationCity = eventSettings?.location?.city ?? location.city;
  const locationCountry = eventSettings?.location?.country ?? location.country;
  const locationVenue = eventSettings?.location?.venue ?? location.venue;
  const pinHref = eventSettings?.location?.maps_url || undefined;
  // rsvp_deadline is admin-editable since v2. Prefer the API value (so a
  // deadline change shows up on the public landing); fall back to the seed
  // when the prop is missing so the first synchronous render still matches
  // EVENT_DATA byte-for-byte (zero CLS).
  const rsvpDeadlineHuman = eventSettings?.rsvp_deadline
    ? formatRsvpDeadline(eventSettings.rsvp_deadline)
    : rsvp.deadline.humanLong;
  // Once the deadline has passed the invitation must stop asking guests
  // to confirm attendance — the API rejects those submissions with a
  // 423 (see `backend/src/utils/date.js::isRsvpLocked`). The gate is
  // authoritative ONLY once the settings request resolved: while it is in
  // flight (or failed) the invitation stays visible (fail open). The seed
  // is a text fallback only, never a visibility source of truth, so the
  // RESOLVED deadline is what closes the artwork — an admin deadline
  // change never needs a seed re-sync to close the page.
  const rsvpDeadlinePassed =
    Boolean(eventSettings) &&
    Boolean(eventSettings.rsvp_deadline) &&
    isRsvpDeadlinePassed(resolveRsvpDeadlineIso(eventSettings));
  // Ceremony / reception subset — admin-editable, default to '' so the
  // matching row is hidden from the artwork when the field is unset. The
  // reception pin always renders because location_venue / city / country
  // are non-nullable in EVENT_DATA and the schema.
  const ceremonyVenue = eventSettings?.ceremony?.venue ?? '';
  const ceremonyMapsUrl = eventSettings?.ceremony?.maps_url ?? '';
  const receptionTimeHuman = formatReceptionTime(eventSettings?.reception_time ?? '');

  // Closed state — the guest list is closed, so the whole invitation
  // disappears: no epigraph, no couple, no ceremony/reception, no dress
  // code, no closing. Only the gold frame and the florals remain, plus:
  //   - the closed-list notice (public landing, no children), or
  //   - the greeting + the children slot (RSVP page — the children's own
  //     copy already says the list is closed).
  const artworkClosed =
    confirmedInvitationWindowActive === undefined
      ? rsvpDeadlinePassed
      : !confirmedInvitationWindowActive;
  if (artworkClosed) {
    return (
      <GoldFrame>
        {showFlorals && (
          <>
            <FloralCorner position="tl" src="/superior.png" />
            <FloralCorner position="br" src="/inferior.png" />
          </>
        )}

        <div
          className={`relative z-30 mx-auto flex max-w-[480px] flex-col items-center text-center ${
            children
              ? 'pt-12 pr-6 pb-12 sm:pt-14 sm:pr-10 sm:pb-16 md:pt-16 md:pr-16 md:pb-20 lg:pt-20 xl:pt-24'
              : 'min-h-[240px] justify-center px-2 py-12 sm:min-h-[280px] sm:px-6 sm:py-16'
          }`}
        >
          {preBlessing && <div className={`${children ? 'mb-4' : 'mb-6'} w-full`}>{preBlessing}</div>}

          {!children && (
            <p
              className="w-full max-w-[380px] rounded-md border border-gold/40 bg-creamTop/95 px-4 py-5 font-heading text-xl font-medium leading-relaxed text-ink shadow-sm sm:px-6 sm:text-2xl"
              data-testid="closed-notice"
            >
              La lista de invitados est&aacute; cerrada
            </p>
          )}

          {children && (
            <div className="mt-2 w-full border-t border-gold/30 pt-2 text-left">{children}</div>
          )}
        </div>
      </GoldFrame>
    );
  }

  return (
    <GoldFrame>
      {showFlorals && (
        <>
          <FloralCorner position="tl" src="/superior.png" />
          <FloralCorner position="br" src="/inferior.png" />
        </>
      )}

      <QuoteTag>{epigraph}</QuoteTag>

      <div className="relative z-30 mx-auto flex max-w-[480px] flex-col items-center pt-12 text-center sm:pt-14 md:pt-16 lg:pt-20 xl:pt-24">
        {preBlessing && <div className="mb-6 w-full">{preBlessing}</div>}

        <p className="font-caps text-[13px] uppercase leading-relaxed tracking-[0.28em] text-gold-deep">
          Con la bendici&oacute;n<br />
          de nuestros padres
        </p>

        <h1
          className="mt-10 whitespace-nowrap font-script text-[44px] leading-[1] text-ink sm:text-[96px]"
          style={{ fontFamily: "'Great Vibes', 'Allura', cursive" }}
        >
          {couple.bride}
        </h1>
        <p
          className="-mt-1 whitespace-nowrap font-script text-2xl text-gold sm:text-3xl"
          style={{ fontFamily: "'Great Vibes', 'Allura', cursive" }}
        >
          y
        </p>
        <h1
          className="-mt-2 whitespace-nowrap font-script text-[44px] leading-[1] text-ink sm:text-[96px]"
          style={{ fontFamily: "'Great Vibes', 'Allura', cursive" }}
        >
          {couple.groom}
        </h1>

        <p className="mt-8 max-w-[440px] font-caps text-[13.5px] leading-[1.7] text-ink">
          {bodyParagraph}
        </p>

        <div className="relative mt-10 w-full max-w-[480px]">
          <div className="flex flex-col items-stretch md:flex-row md:divide-x md:divide-gold/40">
            {/* Left column — Ceremonia */}
            <div className="flex flex-1 flex-col items-center gap-4 px-3 py-2">
              <h3 className="font-caps text-[11px] uppercase tracking-[0.28em] text-gold-deep">
                <span className="border-b border-gold/40 pb-1">Ceremonia</span>
              </h3>
              <IconLine icon="calendar" label={dateHuman} />
              <IconLine icon="clock" label={timeHuman} />
              {ceremonyVenue && (
                <IconLine
                  icon="church"
                  label={ceremonyVenue}
                  href={ceremonyMapsUrl}
                />
              )}
            </div>
            {/* Right column — Recepción */}
            <div className="flex flex-1 flex-col items-center gap-4 px-3 py-2">
              <h3 className="font-caps text-[11px] uppercase tracking-[0.28em] text-gold-deep">
                <span className="border-b border-gold/40 pb-1">Recepci&oacute;n</span>
              </h3>
              {receptionTimeHuman && (
                <IconLine icon="clock" label={receptionTimeHuman} />
              )}
              <IconLine
                icon="pin"
                label={`${locationVenue}\n${locationCity}, ${locationCountry}`}
                href={pinHref}
              />
            </div>
          </div>
          {/* Heart motif over the divider on desktop — visually anchors the
              two columns. Hidden below md so it never overlaps the stacked
              layout. */}
          <span
            aria-hidden="true"
            className="absolute left-1/2 top-1/2 hidden -translate-x-1/2 -translate-y-1/2 rounded-full bg-cream-gradient px-1 text-gold-deep md:block"
          >
            <svg
              viewBox="0 0 24 24"
              width={14}
              height={14}
              fill="none"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinejoin="round"
            >
              <path d="M12 21s-7.5-4.6-9.5-9.2C1.1 8 3.4 4.5 7 4.5c2 0 3.4 1 5 3 1.6-2 3-3 5-3 3.6 0 5.9 3.5 4.5 7.3C19.5 16.4 12 21 12 21z" />
            </svg>
          </span>
        </div>

        <p className="mt-10 font-caps text-[13px] uppercase tracking-[0.28em] text-gold-deep">
          Dress Code
        </p>
        <p
          className="mt-2 font-script text-5xl text-gold"
          style={{ fontFamily: "'Great Vibes', 'Allura', cursive" }}
        >
          {dressCode.label}
        </p>
        <p className="mt-4 max-w-[420px] font-caps text-[13px] leading-[1.7] text-ink">
          {dressCode.note}
        </p>

        {!rsvpDeadlinePassed && !hideRsvpPrompt && (
          <>
            <Divider variant="single-heart" className="mt-10" />

            <p className="font-caps text-[12px] uppercase leading-relaxed tracking-[0.2em] text-ink">
              Agradecemos confirmar tu asistencia<br />
              hasta el {rsvpDeadlineHuman}
            </p>
          </>
        )}

        <p className="mt-6 font-caps text-xs italic tracking-wider text-gold-deep lg:mt-6">
          {closing}
        </p>

        <div className="mt-10 mx-auto flex w-full max-w-[440px] items-end justify-start gap-8 md:gap-10">
          <p className="text-left font-caps text-[9px] uppercase leading-relaxed tracking-[0.32em] text-gold-deep">
            El amor<br />
            tambi&eacute;n<br />
            se celebra<br />
            en familia
            <span aria-hidden="true" className="mt-2 block h-px w-5 bg-gold" />
            <span aria-hidden="true" className="mt-1 inline-block">
              ♥
            </span>
          </p>
          <p
            className="-rotate-2 whitespace-nowrap font-script text-[36px] leading-none text-ink sm:text-[52px]"
            style={{ fontFamily: "'Great Vibes', 'Allura', cursive" }}
          >
            &iexcl;Nos vemos!
            <span aria-hidden="true" className="mt-1 block text-center text-base text-gold">
              ♥
            </span>
          </p>
        </div>

        {children && (
          <div className="mt-3 w-full border-t border-gold/30 pt-2 text-left">{children}</div>
        )}
      </div>
    </GoldFrame>
  );
}
